/**
 * Iter 46 · Leads Pendentes de Direcionamento (Diretoria/Supervisão)
 *
 * Lista os leads do MKT que aguardam direcionamento. O diretor escolhe
 * qual vendedora recebe cada lead — se o município ainda não existe,
 * o backend cria automaticamente ao direcionar.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { TIPOS_ORGAO } from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Target, User as UserIcon, Building2, Mail, Send, AlertCircle, Sparkles } from "lucide-react";

const CANAIS_LABEL = {
  trafego_pago: "Tráfego Pago", email_mkt: "E-mail MKT", site: "Site",
  whatsapp: "WhatsApp", indicacao: "Indicação", evento: "Evento", outro: "Outro",
};

export default function LeadsPendentes() {
  const nav = useNavigate();
  const { lastEvent } = useRealtime() || {};
  const [leads, setLeads] = useState([]);
  const [muns, setMuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [busca, setBusca] = useState("");
  const [direcionando, setDirecionando] = useState(null); // lead selecionado
  const [modo, setModo] = useState("existente"); // existente | novo
  const [munEscolhido, setMunEscolhido] = useState("");
  const [vendedoraId, setVendedoraId] = useState("");
  const [tipoOrgao, setTipoOrgao] = useState("Câmara");
  const [cnpj, setCnpj] = useState("");
  const [emailOrg, setEmailOrg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/leads-mkt/pendentes").then((r) => setLeads(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/municipios").then((r) => setMuns(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role === "vendedora"))).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === "lead_pendente_direcionamento") {
      toast.info(`🎯 Novo lead MKT: ${lastEvent.lead_nome} · ${lastEvent.municipio_nome}/${lastEvent.uf}`);
      load();
    }
  }, [lastEvent, load]);

  const leadsFiltrados = useMemo(() => {
    const s = busca.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) =>
      (l.nome || "").toLowerCase().includes(s) ||
      (l.origem_lead_municipio_nome || "").toLowerCase().includes(s) ||
      (l.origem_lead_uf || "").toLowerCase().includes(s),
    );
  }, [leads, busca]);

  const abrirDirecionar = (lead) => {
    setDirecionando(lead);
    // Se tem sugestão de município existente, começa em modo "existente" + pré-seleciona
    if (lead.municipio_hint?.id) {
      setModo("existente");
      setMunEscolhido(lead.municipio_hint.id);
      setVendedoraId(lead.vendedora_sugerida?.id || lead.municipio_hint.vendedora_id || "");
    } else {
      setModo("novo");
      setMunEscolhido("");
      setVendedoraId("");
    }
    setTipoOrgao("Câmara");
    setCnpj("");
    setEmailOrg("");
  };

  const fechar = () => {
    setDirecionando(null);
    setMunEscolhido("");
    setVendedoraId("");
  };

  const confirmar = async () => {
    if (!vendedoraId) { toast.error("Escolha a vendedora"); return; }
    if (modo === "existente" && !munEscolhido) { toast.error("Escolha o município existente"); return; }
    setBusy(true);
    try {
      const payload = { vendedora_id: vendedoraId };
      if (modo === "existente") {
        payload.municipio_id = munEscolhido;
      } else {
        payload.tipo_orgao = tipoOrgao;
        payload.cnpj_orgao = cnpj || null;
        payload.email_orgao = emailOrg || null;
      }
      const r = await api.post(`/leads-mkt/${direcionando.id}/direcionar`, payload);
      const vendedora = users.find((u) => u.id === vendedoraId);
      toast.success(`✓ Lead direcionado para ${vendedora?.nome || "vendedora"} · ${r.data.municipio?.nome}`);
      fechar();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (detail || "Erro ao direcionar");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-pendentes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <Target className="w-8 h-8 text-fuchsia-600" />
            Leads Pendentes
            {leads.length > 0 && (
              <span
                className="ml-1 inline-flex items-center bg-fuchsia-600 text-white rounded-full px-2.5 py-0.5 text-sm font-bold animate-pulse"
                data-testid="leads-pendentes-count"
              >
                {leads.length}
              </span>
            )}
          </h1>
          <p className="text-slate-500 text-sm">
            Leads do MKT aguardando direcionamento. Escolha a vendedora e, se o município for novo,
            ele será criado automaticamente na carteira dela.
          </p>
        </div>
        <div className="max-w-md w-full sm:w-72 md:w-96">
          <Input
            placeholder="Buscar por nome, município ou UF..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            data-testid="input-busca-leads"
          />
        </div>
      </div>

      {leadsFiltrados.length === 0 && (
        <Card className="p-8 rounded-2xl text-center text-slate-400" data-testid="leads-pendentes-vazio">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          {leads.length === 0
            ? "Nenhum lead pendente no momento."
            : `Nenhum lead bate com "${busca}".`}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {leadsFiltrados.map((l) => {
          const hint = l.municipio_hint;
          const vend = l.vendedora_sugerida;
          return (
            <Card key={l.id} className="p-4 rounded-2xl border-2 border-fuchsia-200 bg-fuchsia-50/40 space-y-3" data-testid={`lead-pend-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1">
                    <Target className="w-3.5 h-3.5" /> {CANAIS_LABEL[l.origem_lead_canal] || l.origem_lead_canal}
                  </div>
                  <div className="font-display font-bold text-lg text-slate-900 truncate flex items-center gap-1">
                    <UserIcon className="w-4 h-4 text-slate-400 shrink-0" />
                    {l.nome}
                  </div>
                  <div className="text-xs text-slate-500">{l.funcao}</div>
                </div>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">
                  {l.origem_lead_em ? new Date(l.origem_lead_em).toLocaleDateString("pt-BR") : ""}
                </span>
              </div>

              <div className="rounded-lg bg-white border border-slate-200 p-2.5 text-sm space-y-0.5">
                <div className="flex items-center gap-1 font-semibold text-slate-800">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  {l.origem_lead_municipio_nome} <span className="text-slate-400">/ {l.origem_lead_uf}</span>
                </div>
                {hint ? (
                  <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
                    ✓ Já existe na base
                    {vend ? <span className="text-slate-500 font-normal">— hoje: {vend.nome}</span> : <span className="text-amber-700">— sem vendedora</span>}
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-700 font-bold flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Município novo — será criado ao direcionar
                  </div>
                )}
              </div>

              {(l.whatsapp || l.email) && (
                <div className="text-xs text-slate-600 space-y-0.5">
                  {l.whatsapp && <div>💬 {l.whatsapp}</div>}
                  {l.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</div>}
                </div>
              )}

              {l.origem_lead_curso_interesse && (
                <div className="text-xs">
                  <span className="bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 font-bold">
                    Interesse: {l.origem_lead_curso_interesse}
                  </span>
                </div>
              )}

              {l.origem_lead_observacoes && (
                <div className="text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2">
                  {l.origem_lead_observacoes}
                </div>
              )}

              <Button
                onClick={() => abrirDirecionar(l)}
                className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid={`btn-direcionar-${l.id}`}
              >
                <Send className="w-4 h-4 mr-1" /> Direcionar para vendedora
              </Button>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!direcionando} onOpenChange={(o) => !o && fechar()}>
        <DialogContent className="max-w-lg" data-testid="modal-direcionar-lead">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-fuchsia-600" /> Direcionar lead
            </DialogTitle>
          </DialogHeader>

          {direcionando && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-fuchsia-50 border border-fuchsia-200 p-3">
                <div className="font-bold text-slate-900">{direcionando.nome} · {direcionando.funcao}</div>
                <div className="text-xs text-slate-600">
                  Município informado: <b>{direcionando.origem_lead_municipio_nome} / {direcionando.origem_lead_uf}</b>
                </div>
              </div>

              {/* Modo: usar existente OU criar novo */}
              <div>
                <Label>Município</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={modo === "existente" ? "default" : "outline"}
                    onClick={() => setModo("existente")}
                    className={modo === "existente" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    disabled={!direcionando.municipio_hint}
                    data-testid="btn-modo-existente"
                  >
                    🔎 Existente {direcionando.municipio_hint ? "✓" : "(nenhum bate)"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={modo === "novo" ? "default" : "outline"}
                    onClick={() => setModo("novo")}
                    className={modo === "novo" ? "bg-blue-600 hover:bg-blue-700" : ""}
                    data-testid="btn-modo-novo"
                  >
                    ➕ Criar novo
                  </Button>
                </div>

                {modo === "existente" ? (
                  <div className="mt-2">
                    <Select value={munEscolhido} onValueChange={setMunEscolhido}>
                      <SelectTrigger data-testid="sel-mun-existente"><SelectValue placeholder="Escolher município..." /></SelectTrigger>
                      <SelectContent className="max-h-96">
                        {(direcionando.municipio_hint ? [direcionando.municipio_hint, ...muns.filter((m) => m.id !== direcionando.municipio_hint.id)] : muns).map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.nome} · {m.uf}{m.tipo_orgao ? ` · ${m.tipo_orgao}` : ""}{m.id === direcionando.municipio_hint?.id ? " · ★ sugerido" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Órgão</Label>
                      <Select value={tipoOrgao} onValueChange={setTipoOrgao}>
                        <SelectTrigger data-testid="sel-tipo-orgao"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_ORGAO.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">CNPJ (opcional)</Label>
                      <Input value={cnpj} onChange={(e) => setCnpj(e.target.value)} data-testid="in-cnpj" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">E-mail institucional (opcional)</Label>
                      <Input type="email" value={emailOrg} onChange={(e) => setEmailOrg(e.target.value)} data-testid="in-email-org" />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label>Vendedora responsável *</Label>
                <Select value={vendedoraId} onValueChange={setVendedoraId}>
                  <SelectTrigger data-testid="sel-vendedora"><SelectValue placeholder="Escolher..." /></SelectTrigger>
                  <SelectContent className="max-h-96">
                    {users.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.codigo ? `${v.codigo} · ` : ""}{v.nome}{v.nucleo_nome ? ` · ${v.nucleo_nome}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={fechar} disabled={busy}>Cancelar</Button>
            <Button onClick={confirmar} disabled={busy} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-confirmar-direcionar">
              <Send className="w-4 h-4 mr-1" /> {busy ? "Direcionando..." : "Direcionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
